import { useState } from 'react';
import { Alert, Button, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import { tokens } from '../theme';
import { deriveSlug } from '../lib/deriveSlug';
import { validateOrganisationName } from '../lib/validateOrganisationName';
import { validateSlug } from '../lib/validateSlug';
import { mapCreateOrgError, type CreateOrgErrorView } from '../lib/createOrganisationErrors';
import { validateName } from '../lib/validateName';
import { NameFields } from './NameFields';

// The form sits on the navy /welcome card, where Mantine's default label colour is near-invisible.
const ON_NAVY = {
  label: { color: tokens.white },
  description: { color: tokens.textMuted },
};

export type CreateOrganisationValues = { name: string; slug: string; firstName?: string; lastName?: string };

export type CreateOrganisationFormProps = {
  // Prefilled from the profile's suggestion or the email domain (may be empty for free-mail/unknown).
  initialName: string;
  submitting: boolean;
  // Server-side fault from the container's POST, shown against its field.
  submitError: CreateOrgErrorView | null;
  // Terminal lock after the escalation cap — disables further submits.
  locked?: boolean;
  // Show first/last name fields (the profile says WorkOS has no name for this user).
  askName?: boolean;
  // Server rejection of the names (PUT /me/name invalid_name), shown until either is edited.
  namesError?: string | null;
  onSubmit: (values: CreateOrganisationValues) => void;
};

// Controlled create-organisation form. Owns field state + the slug-touched flag only;
// all rules live in the pure helpers, and the container owns the POST / navigation.
export function CreateOrganisationForm(props: Readonly<CreateOrganisationFormProps>) {
  const { initialName, submitting, submitError, locked = false, askName = false, namesError = null, onSubmit } = props;
  const [name, setName] = useState(initialName);
  const [slug, setSlug] = useState(() => deriveSlug(initialName));
  const [slugTouched, setSlugTouched] = useState(false);
  // The values last sent to the container, so a server field fault can be suppressed once
  // the user edits that field (a stale "URL taken" must not linger on a fresh value).
  const [submitted, setSubmitted] = useState<{ name: string; slug: string } | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submittedNames, setSubmittedNames] = useState<{ firstName: string; lastName: string } | null>(null);

  function handleNameChange(value: string) {
    setName(value);
    if (!slugTouched) setSlug(deriveSlug(value));
  }

  function handleSlugChange(value: string) {
    // Clearing the field hands control back to auto-derivation (AC3).
    if (value === '') {
      setSlugTouched(false);
      setSlug(deriveSlug(name));
    } else {
      setSlugTouched(true);
      setSlug(value);
    }
  }

  const nameFault = name.trim() ? validateOrganisationName(name) : null;
  const slugFault = slug ? validateSlug(slug) : null;
  const firstNameFault = firstName.trim() ? validateName(firstName, 'first name') : null;
  const lastNameFault = lastName.trim() ? validateName(lastName, 'last name') : null;
  const namesEdited = submittedNames !== null
    && (submittedNames.firstName !== firstName || submittedNames.lastName !== lastName);
  const lastNameError = (!namesEdited && namesError) || lastNameFault || undefined;
  const namesValid = !askName || (!validateName(firstName, 'first name') && !validateName(lastName, 'last name'));

  // A server fault on a field wins over the live client-side format message — but only
  // while that field still holds the value that was submitted; once the user edits it,
  // fall back to live validation so the retry the flow invites isn't discouraged.
  function fieldError(field: 'name' | 'slug', value: string, fault: string | null): string | undefined {
    const editedSinceSubmit = submitted !== null && submitted[field] !== value;
    if (submitError?.field === field && !editedSinceSubmit) return submitError.message;
    return fault ? mapCreateOrgError(400, fault).message : undefined;
  }
  const nameError = fieldError('name', name, nameFault);
  const slugError = fieldError('slug', slug, slugFault);
  const systemError = submitError?.field === null ? submitError.message : undefined;

  const canSubmit = !submitting && !locked
    && name.trim().length > 0 && slug.length > 0
    && !validateOrganisationName(name) && !validateSlug(slug) && namesValid;

  function handleSubmit() {
    if (!canSubmit) return;
    setSubmitted({ name, slug });
    if (!askName) {
      onSubmit({ name: name.trim(), slug });
      return;
    }
    setSubmittedNames({ firstName, lastName });
    onSubmit({ name: name.trim(), slug, firstName: firstName.trim(), lastName: lastName.trim() });
  }

  return (
    <Stack gap={20}>
      <Stack gap={6}>
        <Title order={1} style={{ color: tokens.white, fontSize: 24, letterSpacing: -0.3 }}>
          Create your organisation
        </Title>
        <Text size="sm" c={tokens.textMuted} style={{ lineHeight: 1.5 }}>
          Name your organisation and choose its Cloudhouse URL.
        </Text>
      </Stack>

      {systemError && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light"
               styles={{ root: { background: 'rgba(255,0,0,0.06)' } }}>
          {systemError}
        </Alert>
      )}

      {askName && (
        <NameFields
          firstName={firstName}
          lastName={lastName}
          onFirstNameChange={setFirstName}
          onLastNameChange={setLastName}
          firstNameError={firstNameFault ?? undefined}
          lastNameError={lastNameError}
          disabled={submitting}
          inputStyles={ON_NAVY}
        />
      )}

      <TextInput
        label="Organisation name"
        value={name}
        onChange={(e) => handleNameChange(e.currentTarget.value)}
        error={nameError}
        disabled={submitting}
        styles={ON_NAVY}
      />

      <Stack gap={4}>
        <TextInput
          label="Organisation URL"
          value={slug}
          onChange={(e) => handleSlugChange(e.currentTarget.value)}
          error={slugError}
          disabled={submitting}
          styles={ON_NAVY}
        />
        <Text size="xs" c={tokens.textFaint}>
          Your URL will be {slug || 'your-organisation'}.cloudhouse.com
        </Text>
      </Stack>

      <Button
        size="md" radius={10} fullWidth
        rightSection={<IconArrowRight size={16} stroke={2.2} />}
        disabled={!canSubmit}
        loading={submitting}
        onClick={handleSubmit}
        styles={{ root: { background: tokens.green, color: tokens.navyDeeper, border: 'none', height: 46, fontWeight: 700, letterSpacing: 0.2 } }}
      >
        Create organisation
      </Button>
    </Stack>
  );
}
